/**
 * The page history dialog for one mounted markdown page, shown while its
 * history is the one requested (see `webDocumentHistory`). The dialog itself is
 * the desktop's, from collab-client, loaded on first open; this supplies the
 * browser's markdown projection and rich diff, both from the editor entry this
 * page already loaded.
 */
import { useEffect, useMemo, useState } from 'react';
import { loadPageHistory, type CollabHistoryDiffProps } from '@nimbalyst/collab-bundle/docs-ui';
import { DiffPreviewEditor, previewMarkdownRevisionSnapshot } from '@nimbalyst/collab-bundle/editor';
import {
  closeWebDocumentHistory,
  createWebDocumentHistoryController,
  getRequestedWebDocumentHistory,
  useRequestedWebDocumentHistory,
  useWebDocumentHistoryPage,
} from './webDocumentHistory';

type PageHistoryModule = Awaited<ReturnType<typeof loadPageHistory>>;

function previewRevision(contentFormat: string, bytes: Uint8Array): string | null {
  return contentFormat === 'markdown' ? previewMarkdownRevisionSnapshot(bytes) : null;
}

// The diff viewer re-registers its navigation hooks when these change.
const noop = () => {};

function renderDiff({ diffKey, oldText, newText, isMarkdown, onNavigationStateChange }: CollabHistoryDiffProps) {
  return isMarkdown ? (
    <DiffPreviewEditor
      key={diffKey}
      oldMarkdown={oldText}
      newMarkdown={newText}
      onNavigationStateChange={onNavigationStateChange}
      onNavigatePrevious={noop}
      onNavigateNext={noop}
    />
  ) : null;
}

export function WebDocumentHistoryDialog({ documentId }: { documentId: string }) {
  const open = useRequestedWebDocumentHistory() === documentId;
  const page = useWebDocumentHistoryPage(documentId);
  const [historyModule, setHistoryModule] = useState<PageHistoryModule | null>(null);

  useEffect(() => {
    if (!open || historyModule) return;
    let cancelled = false;
    loadPageHistory()
      .then((loaded) => { if (!cancelled) setHistoryModule(loaded); })
      .catch((error: unknown) => {
        console.error('[WebDocumentHistoryDialog] Could not load page history', error);
        if (!cancelled) closeWebDocumentHistory();
      });
    return () => { cancelled = true; };
  }, [historyModule, open]);

  // Leaving the page closes its history; coming back must not reopen it.
  useEffect(() => () => {
    if (getRequestedWebDocumentHistory() === documentId) closeWebDocumentHistory();
  }, [documentId]);

  const controller = useMemo(
    () => (page && historyModule ? createWebDocumentHistoryController(page, historyModule.CollabHistoryClient) : null),
    [historyModule, page],
  );

  if (!open || !historyModule) return null;
  const { CollabHistoryDialog } = historyModule;
  return (
    <CollabHistoryDialog
      controller={controller}
      onClose={closeWebDocumentHistory}
      previewRevision={previewRevision}
      renderDiff={renderDiff}
    />
  );
}
